import { Trans, t } from "@lingui/macro";
import { addDays } from "date-fns";
import { LocalBusinessJsonLd, NextSeo } from "next-seo";
import { useRouter } from "next/compat/router";
import { GetStaticPaths, GetStaticProps, NextPage } from "next/types";
import { ParsedUrlQuery } from "querystring";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { down } from "styled-breakpoints";
import { useBreakpoint } from "styled-breakpoints/react-styled";
import styled from "styled-components";
import { useAccommodationOfferOfUnitGroup } from "~/applications/OfferSearch/Api/useAccommodationOfferOfUnitGroup";
import {
  fetchDestinationPage,
  useDestinationPage,
} from "~/applications/OfferSearch/Api/useDestinationPage";
import {
  fetchDestinationPages,
  useDestinationPages,
} from "~/applications/OfferSearch/Api/useDestinationPages";
import { useGogaillePlacesOfDestination } from "~/applications/OfferSearch/Api/useGogaillePlacesOfDestination";
import { fetchHotelPage } from "~/applications/OfferSearch/Api/useHotelPage";
import { fetchHotelPagesOfDestination } from "~/applications/OfferSearch/Api/useHotelPagesOfDestination";
import {
  fetchUnitGroupBySlug,
  useUnitGroupBySlug,
} from "~/applications/OfferSearch/Api/useUnitGroupBySlug";
import { fetchUnitGroupsOfHotels } from "~/applications/OfferSearch/Api/useUnitGroupsOfHotels";
import { CancellationPolicyId } from "~/applications/OfferSearch/Domain/CancellationPolicyId";
import {
  DestinationMenuEntry,
  DestinationPage,
  destinationToMenuEntry,
} from "~/applications/OfferSearch/Domain/DestinationPage";
import { HotelPage } from "~/applications/OfferSearch/Domain/HotelPage";
import { UnitGroup } from "~/applications/OfferSearch/Domain/UnitGroup";
import BookButton from "~/applications/OfferSearch/Ui/BookingButton";
import ConditionsAndCancellationPolicy from "~/applications/OfferSearch/Ui/OfferDetails/ConditionsAndCancellationPolicy";
import { GogaillePlacesLocation } from "~/applications/OfferSearch/Ui/OfferDetails/GogaillePlacesLocation";
import OfferDetailsTemplate from "~/applications/OfferSearch/Ui/OfferDetails/OfferDetailsTemplate";
import RoomDetailsPictureGallery from "~/applications/OfferSearch/Ui/OfferDetails/PictureGallery";
import RoomDescriptionHeader from "~/applications/OfferSearch/Ui/OfferDetails/RoomDescriptionHeader";
import SharedSpaceFeatures from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeatures";
import SleepingEquipmentSection from "~/applications/OfferSearch/Ui/OfferDetails/SleepingEquipmentSection";
import { SearchSummaryOnMobileSection } from "~/applications/OfferSearch/Ui/OfferSearchLayout";
import PillSearchDrawerForm from "~/applications/OfferSearch/Ui/StayRequestForm/PillSearchDrawerForm";
import TextualStayRequestForm from "~/applications/OfferSearch/Ui/StayRequestForm/TextualStayRequestForm";
import { useOptionalSearchFromQuery } from "~/applications/OfferSearch/Ui/StayRequestForm/useSearch";
import { BrandIdentity } from "~/components/BrandIdentity/BrandIdentity";
import { fetchBrandIdentity } from "~/components/BrandIdentity/useBrandIdentity";
import Footer from "~/components/Footer";
import { Picture } from "~/components/PictureGallery/Picture";
import { PublicPageSeo } from "~/components/Seo";
import { sendGTMEvent } from "~/components/Tracking/GoogleTagManager";
import { formatStreetAddressInOneLine } from "~/core/AddressFormatter";
import StayRequest from "~/core/StayRequest";
import { TimeOfDay } from "~/core/TimeOfDay";
import { AVAILABLE_LOCALES, DEFAULT_LOCALE } from "~/core/locale";
import {
  newBookingEngineSessionRoute,
  offerSearchUnitGroupRoute,
  offersRoute,
} from "~/core/routes";
import { useOptionalStayRequest } from "~/core/useStayRequest";
import useToday from "~/core/useToday";
import { IconOnlyButton } from "~/guidelines/Button";
import Header from "~/guidelines/Header";
import { Close, KeyboardArrowLeft } from "~/guidelines/Icon";
import { CenteredPulseLoader } from "~/guidelines/Loaders";
import mediaQueries from "~/guidelines/Theme/mediaQueries";
import theme from "~/guidelines/Theme/theme";
import { body1, body2 } from "~/guidelines/Typography";
import { Testimonials } from "~/pages/new-home/_components/Testimonials";
import { serverSideTranslations } from "~/translations/serverSideTranslations";
import { ServerSideGeneratedI18nNamespace } from "~/translations/types";
import StayReassurance from "./_components/StayReassurance";
import BabyChair from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeaturesSource/BabyChair.png";
import BulkTea from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeaturesSource/BulkTea.png";
import CoffeeMachine from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeaturesSource/CoffeeMachine.png";
import FilteredWater from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeaturesSource/FilteredWater.png";
import HairDryer from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeaturesSource/HairDryer.png";
import IroningBoard from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeaturesSource/IroningBoard.png";
import Microwave from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeaturesSource/Microwave.png";
import SharedFridge from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeaturesSource/SharedFridge.png";
import SurvivalKit from "~/applications/OfferSearch/Ui/OfferDetails/SharedSpaceFeaturesSource/SurvivalKit.png";

const PUBLIC_URL = process.env.PUBLIC_URL;

const BottomStickyBar = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StickyButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SoldOutContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};

  ${mediaQueries.md()} {
    align-items: center;
  }
`;

const SoldOut = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  ${body2};
  color: ${({ theme }) => theme.color.text.lightTertiary};
  font-weight: 600;

  ${mediaQueries.md()} {
    ${body1};
    font-weight: 600;
    color: ${({ theme }) => theme.color.text.lightTertiary};
  }
`;

const SoldOutDescription = styled.div`
  ${body2};
  color: ${({ theme }) => theme.color.text.lightTertiary};
`;

const NoOfferContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  ${body1};
  color: ${({ theme }) => theme.color.text.lightTertiary};
  font-weight: 600;

  ${mediaQueries.md()} {
    align-items: center;
  }
`;

const CentralArea = styled.div`
  display: none;

  ${mediaQueries.md()} {
    display: flex;
    flex-grow: 1;
    flex-direction: row;
  }
`;

const TestimonialsContainer = styled.section`
  background-color: ${({ theme }) => theme.color.background.lightThird};
`;

const StayReassuranceContainer = styled.section`
  max-width: 1240px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(7, 5)};
  ${mediaQueries.md()} {
    padding: 120px 0;
  }
`;

interface DetailPageParams extends ParsedUrlQuery {
  destinationSlug: string;
  hotelSlug: string;
  unitGroupSlug: string;
}

interface DetailPageProps extends ServerSideGeneratedI18nNamespace {
  locale: AVAILABLE_LOCALES;
  destinationSlug: string;
  hotelSlug: string;
  unitGroupSlug: string;
  prefetchedDestinationPage: DestinationPage;
  prefetchedDestinationPages: Array<DestinationPage>;
  prefetchedUnitGroup: UnitGroup;
  prefetchedHotelPage: HotelPage;
  prefetchedBrandIdentity: BrandIdentity;
}

export const getStaticPaths: GetStaticPaths<DetailPageParams> = async ({
  locales,
}) => {
  const availableDetailPagesPaths = [];

  const destinationPages = await fetchDestinationPages({
    locale: DEFAULT_LOCALE,
  });
  for (const oneDestinationPage of destinationPages) {
    if (false === oneDestinationPage.features.guestCanBookARoom) {
      continue;
    }

    const hotelPages = await fetchHotelPagesOfDestination({
      destinationId: oneDestinationPage.destination.sanityId,
      locale: DEFAULT_LOCALE,
    });

    if (hotelPages.length >= 1) {
      for (const oneLocale of locales as Array<AVAILABLE_LOCALES>) {
        const unitGroups = await fetchUnitGroupsOfHotels({
          locale: oneLocale,
          hotelIds: hotelPages.map((oneLogePage) => oneLogePage.loge.sanityId),
        });

        for (const unitGroup of unitGroups) {
          availableDetailPagesPaths.push({
            params: {
              destinationSlug: oneDestinationPage.seoSlug,
              hotelSlug: unitGroup.loge.slug,
              unitGroupSlug: unitGroup.slug,
            },
            locale: oneLocale,
          });
        }
      }
    }
  }

  return {
    fallback: false,
    paths: availableDetailPagesPaths,
  };
};

export const getStaticProps: GetStaticProps<
  DetailPageProps,
  DetailPageParams
> = async (context) => {
  const locale = (context.locale ?? context.defaultLocale) as AVAILABLE_LOCALES;
  const destinationSlug = context.params?.destinationSlug;
  const hotelSlug = context.params?.hotelSlug;
  const unitGroupSlug = context.params?.unitGroupSlug;

  if (!destinationSlug) {
    throw new Error('The "destinationSlug" param is missing in the context');
  }

  if (!hotelSlug) {
    throw new Error('The "hotelSlug" param is missing in the context');
  }

  if (!unitGroupSlug) {
    throw new Error('The "unitGroupSlug" param is missing in the context');
  }

  const prefetchedDestinationPage = await fetchDestinationPage({
    locale,
    destinationSlug,
  });

  if (prefetchedDestinationPage == undefined) {
    return { notFound: true };
  }

  const prefetchedDestinationPages = await fetchDestinationPages({
    locale,
  });

  const unitGroup = await fetchUnitGroupBySlug({
    locale,
    hotelSlug,
    unitGroupSlug,
  });

  const hotelPage = await fetchHotelPage({
    destinationId: prefetchedDestinationPage.destination.sanityId,
    hotelSlug,
    locale,
  });

  const prefetchedBrandIdentity = await fetchBrandIdentity();

  return {
    props: {
      locale,
      destinationSlug,
      hotelSlug,
      unitGroupSlug: unitGroupSlug,
      prefetchedDestinationPage,
      prefetchedDestinationPages,
      prefetchedUnitGroup: unitGroup,
      prefetchedHotelPage: hotelPage,
      prefetchedBrandIdentity,
      ...(await serverSideTranslations(locale)),
    },
  };
};

const DetailPage: NextPage<DetailPageProps> = ({
  locale,
  destinationSlug,
  hotelSlug,
  unitGroupSlug,
  prefetchedDestinationPage,
  prefetchedDestinationPages,
  prefetchedUnitGroup,
  prefetchedHotelPage,
  prefetchedBrandIdentity,
}: DetailPageProps) => {
  const today = useToday();
  const tomorrow = addDays(today, 1);
  const router = useRouter();
  const initialStayRequest = useOptionalStayRequest();
  const isMdScreen = useBreakpoint(down("md"));
  const isTracked = useRef(false);
  const [
    isScrollingSufficientToOpenTheStickyBar,
    setIsScrollingSufficientToOpenTheStickyBar,
  ] = useState(false);

  useEffect(() => {
    window.addEventListener("scroll", isSticky);
    return () => {
      window.removeEventListener("scroll", isSticky);
    };
  });

  useEffect(() => {
    if (!router || isTracked.current) return;

    sendGTMEvent("view_product", {
      page_path: router.asPath,
      page_location: window.location.href,
      hotel_destination: destinationSlug,
      hotel_name: hotelSlug,
      hotel_unit_group: unitGroupSlug,
    });
    isTracked.current = true;
  }, [router, destinationSlug, hotelSlug, unitGroupSlug]);

  const isSticky = () => {
    const scrollTop = window.scrollY;

    setIsScrollingSufficientToOpenTheStickyBar(scrollTop >= 120);
  };

  const initialCancellationPolicyId = CancellationPolicyId.FLEXIBLE;

  const { destinationPage } = useDestinationPage(
    { locale, destinationSlug },
    prefetchedDestinationPage,
  );

  const { destinationPages } = useDestinationPages(
    {
      locale,
    },
    prefetchedDestinationPages,
  );

  const { unitGroup } = useUnitGroupBySlug(
    {
      locale,
      hotelSlug,
      unitGroupSlug,
    },
    prefetchedUnitGroup,
  );

  const {
    selectedSearchableDestination,
    selectedStayRequest,
    selectedCancellationPolicyId,
  } = useOptionalSearchFromQuery({
    destinationSlug,
    stayRequest: initialStayRequest,
    cancellationPolicyId: initialCancellationPolicyId,
    destinations:
      destinationPages?.map(destinationToMenuEntry) ??
      prefetchedDestinationPages.map(destinationToMenuEntry),
  });

  const selectedDestination = useMemo<DestinationMenuEntry>(() => {
    const selectedDestination = prefetchedDestinationPages.find(
      (oneDestination) =>
        oneDestination.seoSlug === selectedSearchableDestination.seoSlug,
    );

    // this if will not be trigger because the same exist in useOptionalSearchFromQuery.
    // But it's needed to clear the undefined type provided by find function
    if (!selectedDestination) {
      throw new Error(
        `Could not find the selected destination in the available destinations.
         Selected slug: ${selectedSearchableDestination.seoSlug}.
         Available: ${prefetchedDestinationPages
           .map((oneDestination) => oneDestination.seoSlug)
           .join(",")}
        `,
      );
    }

    return destinationToMenuEntry(selectedDestination);
  }, [prefetchedDestinationPages, selectedSearchableDestination.seoSlug]);

  const accommodationOffer = useAccommodationOfferOfUnitGroup(
    selectedDestination.sanityId,
    selectedStayRequest,
    prefetchedUnitGroup,
    locale,
    CancellationPolicyId.FLEXIBLE,
  );

  const { gogaillePlaces } = useGogaillePlacesOfDestination(
    selectedDestination.sanityId,
  );

  const gallery: Array<Picture> = useMemo(
    () =>
      prefetchedUnitGroup.images.map((oneImages) => {
        return {
          src: oneImages.asset.url,
          alt: oneImages.caption,
          width: oneImages.asset.metadata.dimensions.width,
          height: oneImages.asset.metadata.dimensions.height,
        };
      }),
    [prefetchedUnitGroup.images],
  );

  if (!destinationPage || !destinationPages || !unitGroup || !router) {
    return (
      <>
        <OfferDetailsTemplate
          topBar={<Header backgroundShade="lightFirst"></Header>}
        >
          <CenteredPulseLoader />
        </OfferDetailsTemplate>
      </>
    );
  }

  const seoTitle = `Encore Mieux - ${destinationPage.destination.name} - ${unitGroup.loge.name} | ${unitGroup.name}`;

  const seo = (
    <>
      <NextSeo
        title={seoTitle}
        description={unitGroup.description}
        canonical={
          PUBLIC_URL +
          offerSearchUnitGroupRoute(
            destinationSlug,
            hotelSlug,
            unitGroupSlug,
            undefined,
          )
        }
        openGraph={{
          type: "siteweb",
          title: seoTitle,
          description: unitGroup.description,
          url: PUBLIC_URL + router.asPath,
          images: gallery.map((oneImage) => ({
            url: oneImage.src,
            width: oneImage.width,
            height: oneImage.height,
            alt: oneImage.alt,
          })),
        }}
        twitter={{
          cardType: "summary_large_image",
        }}
      />

      <LocalBusinessJsonLd
        id={PUBLIC_URL + router.asPath}
        type="hotel"
        address={{
          city: prefetchedHotelPage.loge.building.address.city,
          postalCode: prefetchedHotelPage.loge.building.address.postalCode,
          countryName: "France",
          streetAddress: formatStreetAddressInOneLine({
            line1: prefetchedHotelPage.loge.building.address.line1,
            line2: prefetchedHotelPage.loge.building.address.line1 ?? undefined,
          }),
        }}
        name={seoTitle}
        geo={{
          latitude:
            prefetchedHotelPage.loge.building.address.coordinate.latitude,
          longitude:
            prefetchedHotelPage.loge.building.address.coordinate.longitude,
        }}
        images={gallery.map((oneImage) => oneImage.src)}
        description={unitGroup.description}
      />
    </>
  );

  const bookingButton = (
    <BookButton
      selectedCancellationPolicyId={selectedCancellationPolicyId}
      accommodationOffer={accommodationOffer}
      onBook={() => {
        if (!selectedStayRequest || !selectedCancellationPolicyId) {
          return;
        }

        router.push(
          newBookingEngineSessionRoute(
            selectedStayRequest,
            unitGroup.loge.id,
            unitGroup.id,
          ),
        );
      }}
      onNewSearch={() => {
        router.push(
          offersRoute(
            destinationSlug,
            selectedStayRequest
              ? selectedStayRequest
              : StayRequest.forTonight(new Date(), 1, 0, 0),
          ),
          undefined,
          { shallow: true },
        );
      }}
    />
  );

  const stickyBar =
    selectedStayRequest !== undefined ? (
      <BottomStickyBar>
        <StickyButtonContainer>{bookingButton}</StickyButtonContainer>
      </BottomStickyBar>
    ) : undefined;

  const topBar = (
    <Header
      backgroundShade="lightFirst"
      shadow
      centralArea={
        <CentralArea>
          {router.isReady ? (
            <TextualStayRequestForm
              destination={selectedDestination}
              stayRequest={selectedStayRequest}
              destinations={destinationPages.map(destinationToMenuEntry)}
              onDestinationChange={(newDestination) => {
                router.push(
                  offersRoute(newDestination.seoSlug, selectedStayRequest),
                  undefined,
                  { shallow: true },
                );
              }}
              onStayRequestChange={(newStayRequest) => {
                router.push(
                  offersRoute(
                    selectedSearchableDestination.seoSlug,
                    newStayRequest,
                  ),
                  undefined,
                  { shallow: true },
                );
              }}
              offerMaxCapacity={unitGroup.maxCapacity}
              onNewSearch={(newGuests, newTravelPurpose) => {
                return offersRoute(
                  destinationSlug,
                  new StayRequest(
                    selectedStayRequest?.checkIn ?? today,
                    selectedStayRequest?.checkOut ?? tomorrow,
                    newGuests.nbAdults,
                    newGuests.nbChildren,
                    newGuests.nbBabies,
                    newTravelPurpose,
                    selectedStayRequest?.promoCode,
                  ),
                );
              }}
            />
          ) : null}
        </CentralArea>
      }
    >
      {bookingButton}
    </Header>
  );

  const searchRow = (
    <SearchSummaryOnMobileSection>
      <IconOnlyButton
        href={offersRoute(
          selectedSearchableDestination.seoSlug,
          selectedStayRequest,
        )}
        iconColor={theme.color.background.lightFirst}
        icon={<KeyboardArrowLeft />}
        kind="quaternary"
        size="large"
      />
      <PillSearchDrawerForm
        destination={selectedDestination}
        destinations={destinationPages.map(destinationToMenuEntry)}
        stayRequest={selectedStayRequest}
        onSearchRoomOffer={(roomsSearch) => {
          router.push(
            offersRoute(
              roomsSearch.destination.seoSlug,
              roomsSearch.stayRequest,
            ),
            undefined,
            { shallow: true },
          );
        }}
        offerMaxCapacity={unitGroup.maxCapacity}
        onNewSearch={(newGuests, newTravelPurpose) => {
          return offersRoute(
            destinationSlug,
            new StayRequest(
              selectedStayRequest?.checkIn ?? today,
              selectedStayRequest?.checkOut ?? tomorrow,
              newGuests.nbAdults,
              newGuests.nbChildren,
              newGuests.nbBabies,
              newTravelPurpose,
              selectedStayRequest?.promoCode,
            ),
          );
        }}
      />
    </SearchSummaryOnMobileSection>
  );

  const soldOutExplanationText = (
    <SoldOutContainer>
      <SoldOut>
        <Trans>
          <Close /> We are victims of our own success
        </Trans>
      </SoldOut>
      <SoldOutDescription>
        <Trans>
          There are no more availabilities for these dates, try to search for
          another period.
        </Trans>
      </SoldOutDescription>
    </SoldOutContainer>
  );

  const noOfferExplanationText = (
    <NoOfferContainer>
      <Trans>Sorry, this room is not available at time.</Trans>
    </NoOfferContainer>
  );

  return (
    <>
      <PublicPageSeo />

      {seo}

      <OfferDetailsTemplate
        isStickBasVisible={
          isMdScreen !== null ? isScrollingSufficientToOpenTheStickyBar : false
        }
        stickyElement={stickyBar}
        topBar={topBar}
        header={
          <RoomDescriptionHeader
            unitGroup={unitGroup}
            destinationName={selectedDestination.name}
            backButton={
              <IconOnlyButton
                href={offersRoute(
                  selectedSearchableDestination.seoSlug,
                  selectedStayRequest
                    ? selectedStayRequest
                    : StayRequest.forTonight(new Date(), 1, 0, 0),
                )}
                iconColor={theme.color.background.darkFirst}
                icon={<KeyboardArrowLeft />}
                kind="tertiary"
                size="large"
              />
            }
            bookingButton={bookingButton}
            explanationText={
              <>
                {accommodationOffer === null
                  ? noOfferExplanationText
                  : undefined}

                {accommodationOffer?.isSoldOut
                  ? soldOutExplanationText
                  : undefined}
              </>
            }
            accessibility={prefetchedHotelPage.accessibility}
          />
        }
        searchRow={searchRow}
        imageGallery={<RoomDetailsPictureGallery gallery={gallery} />}
        footer={
          <Footer
            pressKitUrl={prefetchedBrandIdentity.pressKitUrl}
            currentLocale={locale}
            availableLocales={Object.values(AVAILABLE_LOCALES)}
            onLocaleChange={(newLocale) => {
              router
                .push(
                  offerSearchUnitGroupRoute(
                    selectedSearchableDestination.seoSlug,
                    hotelSlug,
                    unitGroupSlug,
                    selectedStayRequest,
                  ),
                  undefined,
                  { locale: newLocale },
                )
                .then(() => window.scrollTo(0, 0));
            }}
          />
        }
      >
        <SleepingEquipmentSection />

        <StayReassuranceContainer>
          <StayReassurance />
        </StayReassuranceContainer>
        <SharedSpaceFeatures
          features={[
            { image: SharedFridge, label: t`Shared fridge` },
            { image: Microwave, label: t`Microwave` },
            { image: IroningBoard, label: t`Iron` },

            { image: CoffeeMachine, label: t`Coffee machine` },
            { image: BulkTea, label: t`Tea` },
            {
              image: FilteredWater,
              label: t`Filtered still and sparkling water`,
            },

            { image: HairDryer, label: t`Hair dryer` },
            { image: SurvivalKit, label: t`Survival kit` },
            { image: BabyChair, label: t`Baby chair` },
          ]}
        />
        <TestimonialsContainer>
          <Testimonials destinations={destinationPages} />
        </TestimonialsContainer>
        <GogaillePlacesLocation
          loges={
            gogaillePlaces?.loges.filter(
              (loge) => loge.logeId === unitGroup.loge.id,
            ) ?? [
              {
                logeId: unitGroup.loge.id,
                name: unitGroup.loge.name,
                building: {
                  address: unitGroup.loge.address,
                },
              },
            ]
          }
          gogaillePlacesAddressDetails={
            prefetchedHotelPage.gogaillePlacesAddressDetails
          }
          gogaillePlacesDistancesDetails={
            prefetchedHotelPage.gogaillePlacesDistancesDetails
          }
          gogaillePlacesParkingsDetails={
            prefetchedHotelPage.gogaillePlacesParkingsDetails
          }
        />
        <ConditionsAndCancellationPolicy
          arrivalTime={TimeOfDay.fromStringTime(
            unitGroup.loge.policy.arrivalTime,
          )}
          departureTime={TimeOfDay.fromStringTime(
            unitGroup.loge.policy.departureTime,
          )}
          reducedMobilityRooms={
            prefetchedHotelPage.accessibility.reducedMobilityRooms
          }
          elevator={prefetchedHotelPage.accessibility.elevator}
          stairliftPlatform={
            prefetchedHotelPage.accessibility.stairliftPlatform
          }
        />
      </OfferDetailsTemplate>
    </>
  );
};

export default DetailPage;
